import { queryClient } from '../queryClient';
import { getEntity } from '../entities';
import { qk } from '../queryKeys';
import { fetchOrganizerFullDetail } from './organizers';
import * as api from '../backOfficeApi';

// Resolve a detail surface's entity BY ID — what makes a shared/cold URL work.
//   1. seeded entity cache (a list already loaded it) → instant, then
//   2. fetch by id from the backend (colleague opening the link cold).
// Detail keys are `[domain,'detail',id]` — identical to qk.<domain>.detail, so
// entities seeded by list queries and fetched-by-id share ONE cache entry.

type DetailKey = (id: string) => readonly unknown[];
type ById = (id: string) => Promise<any>;

const detailKey = (domain: string): DetailKey => (id: string) => [domain, 'detail', id] as const;

const seedList = (domain: string, rows: any[]) => {
  for (const r of rows || []) if (r?.id != null) queryClient.setQueryData(detailKey(domain)(String(r.id)), r);
};

/**
 * Seed the by-id cache from a list a PAGE already loaded, so a detail route resolves out of
 * cache instead of re-fetching what is on screen. Exported for surfaces that load their own
 * list outside react-query (ReportsPage) — without it, opening a report from the library
 * would refetch all 93 definitions purely to answer "which one is /reports/:id".
 */
export const seedEntities = seedList;

// Normalize a list-loader's result to its row array. Handles the three shapes
// our loaders return: a bare array, `{ rows }`, or a named-key list like
// `{ agents }` / `{ boxOffices }` (take the first array-valued property).
const extractRows = (res: any): any[] => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.rows)) return res.rows;
  if (res && typeof res === 'object') {
    for (const v of Object.values(res)) if (Array.isArray(v)) return v as any[];
  }
  return [];
};

/**
 * A LOADER THAT DOES NOT EXIST MUST FAIL HERE, NOT AT CLICK TIME.
 *
 * Five of the seventeen entries below named an `api.*` that was never written — loadUserById,
 * findUserByContact, loadPayoutById, loadTransactionAttempt, loadDistributionChannels. Passing
 * `undefined` into these wrappers builds a perfectly good closure, so nothing failed until someone
 * opened a link: the call threw `TypeError: not a function`, the resolver's `.catch(() => null)`
 * swallowed it, and the console said "Nothing with this id exists on the backend" about a customer,
 * a payout, a transaction and a channel that all existed.
 *
 * Checking the REGISTRY afterwards does not catch this — `typeof entry.byId` is always 'function',
 * because the missing loader is hidden inside the closure. (I wrote that version first and it
 * passed while a loader was deliberately broken.) The undefined is only visible HERE, at the
 * moment the wrapper is built, which is module load — so a typo is a startup crash nobody can
 * miss instead of a per-link 404 that reads like a data problem.
 */
const requireLoader = (domain: string, loader: any): any => {
  if (typeof loader !== 'function') {
    throw new Error(
      `entityById: the loader for "${domain}" is ${typeof loader}, not a function. ` +
      `A cold detail link for this domain would report the record does not exist. ` +
      `Check that the api.* function it names is actually exported from lib/backOfficeApi.`,
    );
  }
  return loader;
};

// Fetch the (already-normalized) list, seed every row, return the one for `id`.
// Works for arbitrary ids on page 1 and warms the whole domain cache (sentient).
const listFind = (domain: string, loaderArg: any, args: any = {}): ById => {
  const loader = requireLoader(domain, loaderArg);
  return async (id) => {
  const res = await loader(args);
  const rows = extractRows(res);
  seedList(domain, rows);
  return rows.find((r: any) => String(r?.id) === String(id)) || null;
  };
};

// A true by-id endpoint.
const byIdLoader = (domain: string, loaderArg: any): ById => {
  const loader = requireLoader(domain, loaderArg);
  return async (id) => {
    const o = await loader(id);
    if (o?.id != null) queryClient.setQueryData(detailKey(domain)(String(o.id)), o);
    return o || null;
  };
};

// A "customer" is TWO genuinely different things, and this resolver exists to
// tell them apart rather than paper over the difference:
//
//   1. A real better-auth account (Order.userId, stamped at checkout). This is
//      an actual row with a real primary key — fetch it by that key, same as
//      every other real entity in this REGISTRY (byIdLoader). One request,
//      forever correct, no scanning.
//   2. A genuine guest — no order ever stamped a userId, so no account exists
//      to open. The only honest thing left to show is their order history,
//      found by searching the Order-derived aggregate on their contact info.
//      This is NOT the same kind of result as #1 and must never be dressed up
//      to look like an opened account.
//
// `id` here is whatever routeId the caller had: `userId` when one of their
// orders carried it (listCustomers/PeopleAndUsersPage/OrderDetailPage all
// prefer that now), otherwise a phone/email contact string.
const looksLikeContact = (id: string) => id.includes('@') || /^\+?\d{6,}$/.test(id);

const customerByContact: ById = async (id) => {
  const raw = String(id || '').trim();
  if (!raw) return null;

  if (!looksLikeContact(raw)) {
    // A real account id — the canonical path. Enrich with real order/spend
    // history when we have contact info to search on, but the account fields
    // themselves come from the one real row, not an aggregate guess.
    const account = await api.loadUserById(raw).catch(() => null);
    if (account) {
      const contact = account.phone || account.email;
      const stats = contact
        ? await api.listCustomers({ search: contact, limit: 5 }).catch(() => null)
        : null;
      const match = stats?.customers.find((c: any) =>
        (account.phone && c.phone === account.phone) || (account.email && c.email === account.email),
      );
      const merged = match ? { ...match, ...account, routeId: raw } : account;
      queryClient.setQueryData(detailKey('customers')(raw), merged);
      return merged;
    }
    // Fell through: a stale/invalid id. Try it as a contact string below —
    // harmless (the search just won't match anything) rather than dead-ending.
  }

  // Guest path (or the account lookup above found nothing): "no order stamped
  // a userId" is NOT the same fact as "no account exists" — the stamp only
  // happens going forward at checkout, so someone who registered AFTER an
  // earlier guest order (or whose account search predates being linked) still
  // has a real account this contact can resolve to. Check for one directly
  // before settling for the lesser aggregate view — this is the join the
  // backend's own customers-route comment already named as the intent.
  const account = await api.findUserByContact(raw).catch(() => null);
  if (account?.id) return customerByContact(account.id);

  // Genuinely no account anywhere for this contact — the Order-derived
  // aggregate (order history, spend) is the most honest thing left to show.
  const { customers } = await api.listCustomers({ search: raw, limit: 5 });
  const needle = raw.toLowerCase();
  const match = customers.find((c: any) =>
    String(c.phone || '').toLowerCase() === needle || String(c.email || '').toLowerCase() === needle,
  ) || customers[0] || null;
  if (match?.userId) return customerByContact(match.userId);
  if (match) queryClient.setQueryData(detailKey('customers')(raw), match);
  return match;
};

// Orders & transactions route by the order UUID (Order.backendId), set as
// `routeId` on each normalized row — so /orders/<uuid> and /transactions/<uuid>
// each resolve in ONE deterministic loadOrderDetail call. The human reference
// (7JL9GRA3R25) stays on `.id` for display; it is NOT routable (the backend can't
// look it up or search by it). A transaction is derived from its order.
const orderById: ById = async (uuid) => {
  const o = await (api.loadOrderDetail as any)(uuid);
  if (o) queryClient.setQueryData(detailKey('orders')(String(uuid)), o);
  return o || null;
};
// Organizers route through the SAME key + fetch as useOrganizerDetails
// (the page header / Profile tab's hook) so a cold URL open and the page's
// own hooks share one in-flight fetch instead of firing the heavy
// organization+members+events+stats endpoint twice on every page load.
// fetchOrganizerFullDetail already seeds the lightweight detailKey('organizers')
// entry too, so breadcrumb/list consumers of that key still see it refreshed.
const organizerFullDetailById: ById = (id) => queryClient.ensureQueryData({
  queryKey: qk.organizers.fullDetail(id),
  queryFn: () => fetchOrganizerFullDetail(id),
});

// A transaction is a PaymentAttempt, so a cold /transactions/<id> resolves THAT — not the
// order it belongs to. This previously ran loadOrderDetail(uuid) → transactionFromOrder,
// synthesising a transaction out of an order (inventing a settlement state, a recon status
// and a fee), which is why the detail page opened on an order uuid.
const transactionById: ById = async (attemptId) => {
  // The LIGHT read: just this attempt, so the page receives a real object carrying its
  // `orderId` and can then fetch the sequence in ONE request. Doing the full read here made a
  // cold open cost three (resolver: id + siblings, then the page: siblings again).
  const txn = await (api.loadTransactionAttempt as any)(attemptId);
  if (txn) queryClient.setQueryData(detailKey('transactions')(String(attemptId)), txn);
  return txn || null;
};

const REGISTRY: Record<string, { domain: string; byId: ById }> = {
  'event-detail':            { domain: 'events',       byId: byIdLoader('events', api.loadEventById) },
  'order-detail':            { domain: 'orders',       byId: orderById },  // routes by order UUID
  'organizer-detail':        { domain: 'organizers',   byId: organizerFullDetailById },
  'organizer-statement':     { domain: 'organizers',   byId: organizerFullDetailById },
  // By ID, not a page-1 scan — see loadPayoutById. listFind is fine for domains whose whole list
  // fits one page; payouts is not one of them, and the difference showed up as a shared link
  // insisting a real payout did not exist.
  'payout-detail':           { domain: 'payouts',      byId: byIdLoader('payouts', api.loadPayoutById) },
  'transaction-detail':      { domain: 'transactions', byId: transactionById },  // routes by PaymentAttempt id
  'venue-detail':            { domain: 'venues',        byId: listFind('venues', api.loadVenues, {}) },
  'event-settlement-detail': { domain: 'settlements',   byId: listFind('settlements', api.loadPayoutSettlements, {}) },
  'box-office-detail':       { domain: 'boxOffices',    byId: listFind('boxOffices', api.loadBoxOffices, {}) },
  'agent-detail':            { domain: 'agents',        byId: listFind('agents', api.loadAgents, {}) },
  'tournament-detail':       { domain: 'tournaments',   byId: listFind('tournaments', api.loadTournaments, {}) },
  'team-detail':             { domain: 'teams',         byId: listFind('teams', api.loadTeams, {}) },
  'request-review':          { domain: 'requests',      byId: listFind('requests', api.loadEventRequestPage, {}) },
  'sales-channel-detail':    { domain: 'channels',       byId: listFind('channels', api.loadDistributionChannels, {}) },
  'guest-detail':            { domain: 'passHolders',   byId: byIdLoader('passHolders', api.loadPassHolder) },
  // Routes by phone (or email) — see customerByContact.
  'customer-detail':         { domain: 'customers',      byId: customerByContact },
  // listFind is DETERMINISTICALLY complete here, not a page-1 scan: GET /api/reports is a
  // bare `findAll` with no limit (93 definitions on prod), so every id is in the one response.
  // This is the case the payout-detail note above warns about, checked rather than assumed.
  // ReportsPage seeds these same keys from the list it already loads, so opening a report
  // from the library resolves out of cache without a second request.
  'report-detail':           { domain: 'reports',        byId: listFind('reports', api.loadReportDefinitions, {}) },
};

/** Synchronous cache read for the current detail surface (instant, no fetch). */
export function getRouteEntity(active: string, id: string | null | undefined) {
  const reg = REGISTRY[active];
  return reg && id ? getEntity(detailKey(reg.domain), id) : undefined;
}

/** Resolve (cache or fetch-by-id) the entity for a detail surface. */
export async function resolveRouteEntity(active: string, id: string): Promise<any> {
  const reg = REGISTRY[active];
  if (!reg || !id) return null;
  try {
    return await queryClient.ensureQueryData({ queryKey: detailKey(reg.domain)(id), queryFn: () => reg.byId(id) });
  } catch {
    return null;
  }
}

export const COLD_LINKABLE = new Set(Object.keys(REGISTRY));
